// src/pages/operations-dashboard/components/CourierSplitSection.jsx
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { Truck, BarChart3 } from "lucide-react";
import {
  formatCompact,
  formatPercent,
  formatShortDate,
  CHART_COLORS,
} from "../../../utils/dashboardConstants";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <p className="text-sm font-semibold text-gray-800 mb-1">{d?.carrier_name || d?.name}</p>
      <div className="flex items-center gap-3">
        <span className="text-lg font-bold text-gray-900">{formatCompact(d?.total_count || d?.value)}</span>
        {d?.percentage !== undefined && (
          <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{formatPercent(d.percentage)}</span>
        )}
      </div>
    </div>
  );
};

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function CourierSplitSection({ data, loading }) {
  const [viewMode, setViewMode] = useState("bar");
  const isLoading = loading?.courierSplit;

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (!data) return null;

  const carriers = (data.carrier_counts || [])
    .map((c, i) => ({
      name: c.carrier_name || c.carrier_partner_id || `Carrier ${i + 1}`,
      value: c.total_count || 0,
      percentage: c.percentage || 0,
      delivered: c.delivered_count || 0,
      rto: c.rto_count || 0,
      carrier_name: c.carrier_name || c.carrier_partner_id,
      total_count: c.total_count || 0,
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-cyan-50">
            <Truck className="w-4.5 h-4.5 text-cyan-500" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Courier Partner Split</h3>
            <p className="text-xs text-gray-400 mt-0.5">Shipment distribution across carriers</p>
          </div>
        </div>

        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode("bar")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              viewMode === "bar" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Bar
          </button>
          <button
            onClick={() => setViewMode("pie")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              viewMode === "pie" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Pie
          </button>
        </div>
      </div>

      <div className="p-6">
        {carriers.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
            No courier data available
          </div>
        ) : viewMode === "bar" ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={carriers} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                    tickFormatter={formatCompact}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={28}>
                    {carriers.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Carrier table */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Breakdown</p>
              {carriers.map((c, i) => (
                <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{c.name}</p>
                    <div className="w-full h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(c.percentage, 100)}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-gray-800">{formatCompact(c.value)}</p>
                    <p className="text-[10px] text-gray-400">{formatPercent(c.percentage)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={carriers}
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={50}
                  paddingAngle={2}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {carriers.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="white" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, paddingTop: 16 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
